import { useState, useEffect } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import { Skeleton } from '../../components/ui/skeleton';
import { api } from '../../lib/api';
import { WarehouseListCard } from './components/WarehouseListCard';
import type { WarehouseCar } from '../../types/car';

export default function WarehouseIndex() {
  const { t } = useLanguage();
  const [warehouseCars, setWarehouseCars] = useState<WarehouseCar[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const warehouseRes = await api.get('/api/warehouse-cars?status=IN_STOCK&limit=100');
        if (cancelled) return;
        const wh = (warehouseRes as { warehouseCars?: WarehouseCar[] })?.warehouseCars ?? warehouseRes;
        setWarehouseCars(Array.isArray(wh) ? wh : []);
      } catch (e) {
        console.error(e);
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (loading && warehouseCars.length === 0) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <Skeleton className="h-12 w-48 rounded-lg" />
          <span className="text-sm text-slate-500">{t('common.loading')}</span>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-slate-200">
        <div className="w-full px-4 sm:px-6 lg:px-8 py-6 flex flex-wrap items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-semibold text-slate-900 tracking-tight">{t('warehouse.title')}</h1>
            <p className="text-slate-500 text-sm mt-0.5">{t('warehouse.subtitle')}</p>
          </div>
        </div>
      </header>

      <main className="w-full px-4 sm:px-6 lg:px-8 py-8">
        <WarehouseListCard warehouseCars={warehouseCars} />
      </main>
    </div>
  );
}
